// AI Service Client - HuggingFace Space (OpenAI-compatible streaming API)
const AI_BASE_URL = import.meta.env.VITE_AI_URL || 'https://hellosguhs-opcodhfprivate.hf.space/v1';
const HF_TOKEN = import.meta.env.VITE_HF_TOKEN || '';
const DEFAULT_MODEL = 'opencode/auto';

export interface AIParseOptions {
  model?: string;
  temperature?: number;
  max_tokens?: number;
}

export interface ParsedStudentData {
  studentName?: string;
  gender?: string;
  dateOfBirth?: string;
  className?: string;
  sectionName?: string;
  rollNo?: string;
  fatherName?: string;
  motherName?: string;
  fatherPhone?: string;
  motherPhone?: string;
  whatsappNumber?: string;
  address?: string;
  villageTown?: string;
  district?: string;
  schoolName?: string;
  schoolBoard?: string;
  previousClassBoard?: string;
  previousMarks?: string;
  previousPercentage?: string;
}

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface ChatStreamCallbacks {
  onChunk: (text: string) => void;
  onDone: () => void;
  onError: (err: Error) => void;
}

async function parseJSONFromResponse(content: string): Promise<any> {
  let cleaned = content
    .replace(/^```(?:json)?\s*\n?/i, '')
    .replace(/\n?```\s*$/i, '')
    .trim();
  const match = cleaned.match(/\{[\s\S]*\}/);
  if (match) cleaned = match[0];
  return JSON.parse(cleaned);
}

export const aiService = {
  async parseStudentFromOCR(ocrText: string, options?: AIParseOptions): Promise<ParsedStudentData | null> {
    const prompt = `Extract student admission details from the following OCR text and return ONLY a valid JSON object with these fields (use empty string for missing data):
- studentName
- gender (male/female)
- dateOfBirth (YYYY-MM-DD format)
- className (e.g., "10", "11")
- sectionName (e.g., "A", "B")
- rollNo
- fatherName
- motherName
- fatherPhone
- motherPhone
- whatsappNumber
- address
- villageTown
- district
- schoolName
- schoolBoard (e.g., CBSE, ICSE, State Board)
- previousClassBoard
- previousMarks
- previousPercentage

OCR TEXT:
${ocrText}

Respond ONLY with the JSON object, no other text.`;

    try {
      const response = await fetch(`${AI_BASE_URL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(HF_TOKEN ? { 'Authorization': `Bearer ${HF_TOKEN}` } : {}),
        },
        body: JSON.stringify({
          model: options?.model || DEFAULT_MODEL,
          messages: [
            { role: 'system', content: 'You are a helpful assistant that extracts structured student data from OCR text. Return only valid JSON.' },
            { role: 'user', content: prompt },
          ],
          temperature: options?.temperature ?? 1.0,
          max_tokens: options?.max_tokens ?? 2000,
          stream: false,
        }),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const content = data.choices?.[0]?.message?.content?.trim() || '';
      return parseJSONFromResponse(content) as ParsedStudentData;
    } catch {
      return null;
    }
  },

  /** Stream a chat completion. Returns a cleanup function to abort. */
  streamChat(messages: ChatMessage[], callbacks: ChatStreamCallbacks, options?: AIParseOptions): () => void {
    const controller = new AbortController();

    (async () => {
      try {
        const response = await fetch(`${AI_BASE_URL}/chat/completions`, {
          method: 'POST',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            ...(HF_TOKEN ? { 'Authorization': `Bearer ${HF_TOKEN}` } : {}),
          },
          body: JSON.stringify({
            model: options?.model || DEFAULT_MODEL,
            messages,
            temperature: options?.temperature ?? 1.0,
            max_tokens: options?.max_tokens ?? 2000,
            stream: true,
          }),
        });

        if (!response.ok || !response.body) {
          const text = await response.text().catch(() => 'Unknown error');
          throw new Error(`AI API error ${response.status}: ${text}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed.startsWith('data: ') || trimmed === 'data: [DONE]') continue;
            const jsonStr = trimmed.slice(6);
            if (!jsonStr) continue;
            try {
              const chunk = JSON.parse(jsonStr);
              const text = chunk.choices?.[0]?.delta?.content || '';
              if (text) callbacks.onChunk(text);
            } catch {
              // ignore malformed SSE lines
            }
          }
        }

        callbacks.onDone();
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          callbacks.onError(err);
        }
      }
    })();

    return () => controller.abort();
  },

  /** Non-streaming chat for simple prompts */
  async chat(messages: ChatMessage[], options?: AIParseOptions): Promise<string> {
    const response = await fetch(`${AI_BASE_URL}/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(HF_TOKEN ? { 'Authorization': `Bearer ${HF_TOKEN}` } : {}),
      },
      body: JSON.stringify({
        model: options?.model || DEFAULT_MODEL,
        messages,
        temperature: options?.temperature ?? 1.0,
        max_tokens: options?.max_tokens ?? 2000,
        stream: false,
      }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`AI API error ${response.status}: ${text}`);
    }

    const data = await response.json();
    return data.choices?.[0]?.message?.content?.trim() || '';
  },
};
